import { isSameDay, parseISO, startOfDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";
import { rangeContainsModifiers } from "react-day-picker";
import DateRangeCalendar from "@/components/DateRangeCalendar";
import { useCampground } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";

export default function Dates() {
  const { state, actions } = useBookingContext();
  const { data: campgroundData } = useCampground();

  const isDateDisabled = useMemo(() => {
    const periods = campgroundData.campground.seasons.flatMap((season) =>
      season.time_periods.map((period) => ({
        start: parseISO(period.date_start),
        end: parseISO(period.date_end),
        isLock: season.is_lock,
      })),
    );

    return (date: Date) => {
      const day = startOfDay(date);
      if (day < startOfDay(new Date())) return true;

      // Season boundaries are inclusive, and locks override overlapping open seasons.
      const matchingPeriods = periods.filter((period) => day >= period.start && day <= period.end);
      return (
        matchingPeriods.some((period) => period.isLock) ||
        !matchingPeriods.some((period) => !period.isLock)
      );
    };
  }, [campgroundData.campground.seasons]);

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    state.dateFrom && state.dateUntil ? { from: state.dateFrom, to: state.dateUntil } : undefined,
  );

  useEffect(() => {
    // sync selected range to context
    if (
      dateRange?.from &&
      dateRange?.to &&
      !isSameDay(dateRange.from, dateRange.to) &&
      !rangeContainsModifiers({ from: dateRange.from, to: dateRange.to }, isDateDisabled)
    ) {
      actions.setDateFrom(dateRange.from);
      actions.setDateUntil(dateRange.to);
    } else {
      actions.setDateFrom(null);
      actions.setDateUntil(null);
    }
  }, [dateRange, actions, isDateDisabled]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO
  useEffect(() => {
    // handle state reset
    if (
      // range is empty in context
      !state.dateFrom &&
      !state.dateUntil &&
      // not empty in component
      dateRange?.from &&
      dateRange?.to &&
      // selected more than one day, because in this case
      // we also don't store range in context
      dateRange.from !== dateRange.to
    ) {
      setTimeout(() => setDateRange(undefined));
    }
  }, [state.dateFrom, state.dateUntil]);

  return (
    <DateRangeCalendar
      dateRange={dateRange}
      onSelect={setDateRange}
      disabled={isDateDisabled}
      excludeDisabled
      defaultMonth={dateRange?.from || new Date()}
      sx={{
        "& .rdp-root": {
          borderWidth: "1px 0px 0px 0px",
          borderColor: "divider",
          borderStyle: { xs: "none", sm: "solid" },
          height: { xs: "auto", sm: "100%" },
        },
      }}
    />
  );
}
